import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";
import { getSessionId, setUserId } from "../lib/utm";
import { setInviteSlug, setInviteTag } from "../lib/invite";
import MobileCaptureDialog from "../components/MobileCaptureDialog";
import BrandHeader from "../components/BrandHeader";
import { toast } from "sonner";

/**
 * Recipient-facing landing for admin-created invites.
 * Routes:
 *   /i/:slug            — single (personal) invite
 *   /m/:slug            — master invite, untagged
 *   /m/:slug/:tag       — master invite, path-style variable
 *   /m/:slug?v=<tag>    — master invite, query-style variable
 *
 * Behaviour:
 *  1. Resolves the slug via /api/invites/resolve/:slug (public, tag-aware).
 *  2. Fires /api/invites/:slug/open (idempotent; increments open_count).
 *     For master invites the funnel row is keyed by (slug, tag).
 *  3. Stashes slug + tag in sessionStorage so downstream pages can attach
 *     `invite_slug`/`invite_tag` to /customers/identify + /track/click.
 *  4. Routes based on the invite shape:
 *     - product-specific + skip_capture=true  → auto-identify with the invite
 *       phone, fire /track/click, redirect straight to the partner. Dialog
 *       never opens. (single invites only — masters never skip capture)
 *     - product-specific + skip_capture=false → open the branded
 *       MobileCaptureDialog for that product, phone prefilled when known.
 *     - generic (no product_id)               → redirect to the loans grid
 *       with ?invite=<slug>&v=<tag> so the grid's apply flow attaches them.
 */
export default function InviteLanding() {
  const { slug, tag: routeTag } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading" });

  // Variable can arrive as a path segment (/m/slug/tag) or query (?v=tag).
  const tag = (() => {
    if (routeTag) return routeTag;
    try {
      return new URLSearchParams(window.location.search).get("v") || "";
    } catch (_) {
      return "";
    }
  })();
  const tagQ = tag ? `?tag=${encodeURIComponent(tag)}` : "";

  useEffect(() => {
    let cancelled = false;

    async function run() {
      setInviteSlug(slug);
      if (tag) setInviteTag(tag);
      // Fire-and-forget open beacon — never block on it.
      api.post(`/invites/${slug}/open${tagQ}`).catch(() => { /* silent */ });

      let resolved;
      try {
        const { data } = await api.get(`/invites/resolve/${slug}${tagQ}`);
        if (cancelled) return;
        resolved = data;
      } catch (err) {
        if (cancelled) return;
        setState({ status: "notfound" });
        return;
      }

      // Branch 1: generic invite → hand off to the loans grid.
      if (!resolved.product_id) {
        const tagParam = tag ? `&v=${encodeURIComponent(tag)}` : "";
        navigate(`/?loans=1&invite=${slug}${tagParam}`, { replace: true });
        return;
      }

      // Branch 2: product-specific + skip_capture → straight-through redirect.
      if (resolved.skip_capture && resolved.customer_phone) {
        setState({ status: "redirecting", invite: resolved });
        try {
          // Identify with the invite's phone so the click record carries the
          // real customer identity + a HITCH-USER id downstream.
          await api.post("/customers/identify", {
            phone: resolved.customer_phone,
            name: resolved.customer_name || undefined,
            session_id: getSessionId(),
            invite_slug: slug,
            ...(tag ? { invite_tag: tag } : {}),
          }).then(({ data }) => {
            if (data?.hitch_user_id) setUserId(data.hitch_user_id);
          });
          const { data: click } = await api.post("/track/click", {
            product_id: resolved.product_id,
            session_id: getSessionId(),
            invite_slug: slug,
            ...(tag ? { invite_tag: tag } : {}),
          });
          if (click?.tracked_redirect_url) {
            window.location.replace(click.tracked_redirect_url);
            return;
          }
        } catch (err) {
          // Fall through to showing the dialog so the customer can still
          // manually proceed rather than hitting a dead end.
          toast.error("Redirect failed — please continue manually.");
        }
      }

      // Branch 3 (default): open the branded mobile-capture dialog.
      setState({ status: "capture", invite: resolved });
    }

    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, tag, navigate]);

  if (state.status === "loading" || state.status === "redirecting") {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#0055FF] mb-3" />
          <p className="text-sm text-neutral-600" data-testid="invite-loading">
            {state.status === "redirecting" ? "Redirecting to lender…" : "Loading your invite…"}
          </p>
        </div>
      </div>
    );
  }

  if (state.status === "notfound") {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="max-w-sm text-center" data-testid="invite-notfound">
          <div className="w-14 h-14 rounded-2xl bg-red-50 mx-auto mb-4 grid place-items-center">
            <ShieldAlert className="w-7 h-7 text-red-500" />
          </div>
          <h1 className="font-display text-2xl tracking-tight mb-2">Invite not found</h1>
          <p className="text-sm text-neutral-600 mb-6">
            This link may have expired or been mistyped. Ask the person who shared it to send a fresh invite.
          </p>
          <a href="/" className="text-sm underline underline-offset-4 text-[#0055FF]">
            Go to Offer Express
          </a>
        </div>
      </div>
    );
  }

  // status === "capture" — render personal invite banner + branded dialog.
  const invite = state.invite;
  const product = invite.product;
  const isMaster = invite.invite_type === "master";
  const productWithPrefill = {
    ...product,
    // Handed to MobileCaptureDialog so it renders the hero and prefills phone.
    _invite_customer_name: invite.customer_name,
    _invite_customer_phone: invite.customer_phone,
    _invite_slug: invite.slug,
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-8" data-testid="invite-landing">
      {/* Show the BrandHeader in the page body too so the recipient sees the
          branded partner context even before they open the modal (in case the
          dialog is delayed). Also gives us a canonical hero + a personalised
          greeting strip. */}
      <div className="w-full max-w-md">
        <div className="rounded-t-2xl px-4 py-2.5 text-[13px] font-semibold flex items-center justify-between bg-gradient-to-r from-[#f0f4ff] to-[#e0e7ff] text-[#0055FF] border border-b-0 border-[#e0e7ff]">
          <span data-testid="invite-greeting">
            👋 {invite.customer_name ? `Hi ${invite.customer_name}` : "Hi there"} · {isMaster ? "Exclusive offer" : "Personal invite"}
          </span>
          <span className="text-[11px] text-neutral-500 font-medium">Just for you</span>
        </div>
        <div className="rounded-b-2xl border border-t-0 border-neutral-200 bg-white shadow-sm">
          <BrandHeader product={product} />
          <div className="px-6 pb-6 -mt-4">
            <p className="text-sm text-neutral-600 text-center">
              Tap below to continue to {product.provider || product.title}.
            </p>
            <button
              type="button"
              data-testid="invite-open-dialog"
              onClick={() => setState((s) => ({ ...s, dialogOpen: true }))}
              className="mt-4 w-full h-11 rounded-lg text-white text-sm font-semibold transition-opacity hover:opacity-90"
              style={{ backgroundColor: "#111" }}
            >
              Verify mobile & continue
            </button>
          </div>
        </div>
      </div>

      <MobileCaptureDialog
        open={!!state.dialogOpen}
        onOpenChange={(v) => setState((s) => ({ ...s, dialogOpen: v }))}
        product={productWithPrefill}
        inviteSlug={invite.slug}
        inviteTag={isMaster ? tag : undefined}
        prefilledPhone={invite.customer_phone}
        badge={isMaster ? "Exclusive offer" : "Personal invite"}
        onCaptured={async () => {
          try {
            const { data: click } = await api.post("/track/click", {
              product_id: product.id,
              session_id: getSessionId(),
              invite_slug: invite.slug,
              ...(tag ? { invite_tag: tag } : {}),
            });
            if (click?.tracked_redirect_url) {
              window.location.replace(click.tracked_redirect_url);
            }
          } catch (err) {
            toast.error("Redirect failed — please retry.");
          }
        }}
      />
    </div>
  );
}
